import axios from 'axios';
import {
  AML_VERIFIED_DATA,
  CIPC_VERIFIED_DATA,
  CREDIT_REPORT_VERIFIED_DATA,
  GET_LINKED_ONBOARDING_FORMS_API,
  GET_QUESTION_MAPPINGS,
  GET_VERIFICATION_FORMS,
  GET_VERIFICATION_LOGS,
  GET_VERIFICATION_SERVICE_BINDINGS,
  GET_VERIFICATION_SERVICES,
  GET_VERIFICATION_STATUSES,
  IDV_VERIFIED_DATA,
  KYC_VERIFIED_DATA,
  RUN_AML_VERIFICATION,
  RUN_CIPC_VERIFICATION,
  RUN_CREDIT_REPORT_VERIFICATION,
  RUN_IDENTITY_VERIFICATION,
  RUN_KYC_VERIFICATION,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import { fetch_requestor_full_config_by_entity_type } from '@/services/requestor_portal_service';
import { selectedTenantVerificationServiceIds } from '@/services/tenant_verification_preferences';

function parse_json(value, fallback = {}) {
  try { return JSON.parse(value); }
  catch { return fallback; }
}

function parse_payload(response) {
  const payload = response?.data ?? response ?? {};
  return typeof payload === 'string' ? parse_json(payload) : payload;
}

function payload_data(response) {
  const payload = parse_payload(response);
  const data = payload?.data ?? payload;
  return typeof data === 'string' ? parse_json(data) : data;
}

function collection(response) {
  const payload = payload_data(response);
  const candidates = [payload?.data, payload?.results, payload?.items, payload];
  return candidates.find(Array.isArray) || [];
}

function positive_number(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function normalize_name(value) {
  return String(value || '').trim().toLowerCase();
}

function title_case(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function service_id(service = {}) {
  return positive_number(service?.id || service?.service_id);
}

function service_name(service = {}) {
  return String(service?.name || service?.service_name || service?.title || 'Verification check').trim();
}

function application_form_id(form = {}) {
  return positive_number(form?.application_form_id || form?.applicationFormId || form?.id);
}

function form_definition_id(form = {}) {
  return positive_number(form?.form?.id || form?.form_id || form?.form?.form_id);
}

function entity_config_payload(config = {}) {
  return config?.data && typeof config.data === 'object' ? config.data : config;
}

function binding_service_id(binding = {}) {
  return positive_number(
    binding?.service_id
    || binding?.service?.id
    || binding?.service
    || binding?.verification_service_id
    || binding?.verification_service?.id
    || binding?.verification_service
  );
}

async function fetch_services() {
  const response = await axios.get(GET_VERIFICATION_SERVICES, get_request_config());
  return collection(response)
    .map((service) => ({
      ...service,
      id: service_id(service),
      name: service_name(service),
      description: String(service?.description || '').trim(),
    }))
    .filter((service) => service.id);
}

async function fetch_binding_records(config = {}) {
  const embedded = Array.isArray(config?.verification_service_bindings)
    ? config.verification_service_bindings.filter((binding) => binding?.is_active !== false)
    : [];

  const entity_config_id = positive_number(config?.entity_config?.id || config?.entity_config_id);
  if (!entity_config_id) return embedded;

  try {
    const response = await axios.get(
      GET_VERIFICATION_SERVICE_BINDINGS,
      get_request_config({ params: { entity_config_id } })
    );
    return collection(response).filter((binding) => binding?.is_active !== false);
  } catch {
    return embedded;
  }
}

async function fetch_bound_services(entity_type_id, project_id = null) {
  const [services, raw_config] = await Promise.all([
    fetch_services(),
    fetch_requestor_full_config_by_entity_type(entity_type_id, project_id ? { project_id } : {}),
  ]);
  const config = entity_config_payload(raw_config);
  const service_ids = new Set((await fetch_binding_records(config)).map(binding_service_id).filter(Boolean));
  const tenant_service_ids = new Set(selectedTenantVerificationServiceIds(project_id));
  return services.filter((service) => (
    tenant_service_ids.has(Number(service.id))
    && service_ids.has(Number(service.id))
  ));
}

function verification_form_record(form = {}) {
  return {
    id: positive_number(form?.verification_form_id || form?.id),
    definition_id: positive_number(
      form?.survey_form_id
      || form?.form_id
      || form?.verification_form_definition_id
      || form?.form?.id
    ),
  };
}

async function eligible_definition_ids_for_service(service) {
  const response = await axios.get(GET_VERIFICATION_FORMS, get_request_config());
  const verification_forms = collection(response).map(verification_form_record).filter((form) => form.id);
  const linked_groups = await Promise.all(verification_forms.map(async (verification_form) => {
    try {
      const [links_response, mappings_response] = await Promise.all([
        axios.get(GET_LINKED_ONBOARDING_FORMS_API, get_request_config({
          params: { verification_form_id: verification_form.id },
        })),
        axios.get(GET_QUESTION_MAPPINGS, get_request_config({
          params: { verification_form_id: verification_form.id, service_id: service.id },
        })),
      ]);
      const mappings = payload_data(mappings_response)?.mappings || [];
      if (!Array.isArray(mappings) || !mappings.length) return [];

      return collection(links_response)
        .map((link) => positive_number(link?.onboarding_form_id || link?.onboarding_form?.id))
        .filter(Boolean);
    } catch {
      return [];
    }
  }));
  return new Set(linked_groups.flat());
}

async function runnable_form_for_service(service, forms = []) {
  const eligible_ids = await eligible_definition_ids_for_service(service);
  return forms.find((form) => (
    eligible_ids.has(form_definition_id(form)) && application_form_id(form)
  )) || null;
}

async function fetch_form_statuses(forms = []) {
  const pairs = await Promise.all(forms.map(async (form) => {
    const form_id = application_form_id(form);
    if (!form_id) return [null, {}];
    try {
      const response = await axios.get(GET_VERIFICATION_STATUSES, get_request_config({
        params: { application_form_id: form_id },
      }));
      return [form_id, payload_data(response)?.data || payload_data(response) || {}];
    } catch {
      return [form_id, {}];
    }
  }));
  return new Map(pairs.filter(([form_id]) => form_id));
}

async function fetch_verification_logs(entity_profile_id) {
  if (!entity_profile_id) return [];
  try {
    const response = await axios.get(GET_VERIFICATION_LOGS, get_request_config({
      params: { entity_profile_id },
    }));
    const payload = payload_data(response);
    return Array.isArray(payload?.logs) ? payload.logs : [];
  } catch {
    return [];
  }
}

function service_result_endpoint(service = {}) {
  const name = normalize_name(service_name(service));
  if (name.includes('credit')) return CREDIT_REPORT_VERIFIED_DATA;
  if (name.includes('cipc')) return CIPC_VERIFIED_DATA;
  if (name.includes('aml')) return AML_VERIFIED_DATA;
  if (name.includes('identity') || name.includes('id verification') || name.includes('idv')) return IDV_VERIFIED_DATA;
  if (name.includes('kyc')) return KYC_VERIFIED_DATA;
  return '';
}

function service_run_endpoint(service = {}) {
  const name = normalize_name(service_name(service));
  if (name.includes('credit')) return RUN_CREDIT_REPORT_VERIFICATION;
  if (name.includes('cipc')) return RUN_CIPC_VERIFICATION;
  if (name.includes('aml')) return RUN_AML_VERIFICATION;
  if (name.includes('identity') || name.includes('id verification') || name.includes('idv')) return RUN_IDENTITY_VERIFICATION;
  if (name.includes('kyc')) return RUN_KYC_VERIFICATION;
  return '';
}

async function fetch_latest_service_result(service, entity_profile_id) {
  const endpoint = service_result_endpoint(service);
  if (!endpoint || !entity_profile_id) return null;
  try {
    const response = await axios.get(endpoint, get_request_config({
      params: { entity_profile_id, service_id: service.id },
    }));
    const result = payload_data(response);
    return result && typeof result === 'object' ? result : null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

function status_tone(status = '') {
  const normalized = normalize_name(status);
  if (['success', 'pass', 'passed', 'verified', 'clean', 'complete'].some((word) => normalized.includes(word))) return 'match';
  if (['fail', 'mismatch', 'rejected', 'error', 'blocked'].some((word) => normalized.includes(word))) return 'mismatch';
  if (['pending', 'review', 'queued', 'running'].some((word) => normalized.includes(word))) return 'pending';
  return 'neutral';
}

function match_state(result = {}) {
  if (result?.match === true) return 'match';
  if (result?.match === false) return 'mismatch';
  return status_tone(result?.match_status || result?.status || result?.outcome);
}

function display_value(value) {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value !== 'object') return String(value);
  try { return JSON.stringify(value); }
  catch { return String(value); }
}

function result_rows(result = {}) {
  const fields = result?.field_results;
  if (!fields || typeof fields !== 'object') return [];

  return Object.entries(fields).map(([field, detail]) => {
    const tone = match_state(detail);
    return {
      field: title_case(field),
      submitted_value: display_value(detail?.user_answer),
      provider_value: display_value(detail?.api_value),
      status: tone === 'match' ? 'Match' : tone === 'mismatch' ? 'Mismatch' : 'Pending',
      tone,
    };
  });
}

function latest_log_for_service(logs = [], service = {}) {
  return logs.find((log) => Number(log?.service_id) === Number(service.id)) || null;
}

function format_timestamp(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-ZA', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function status_for_service(statuses, runnable_form, service) {
  const form_statuses = statuses.get(application_form_id(runnable_form)) || {};
  return Reflect.get(form_statuses, String(service.id)) || Reflect.get(form_statuses, service.id) || null;
}

async function verification_check(service, context) {
  const runnable_form = await runnable_form_for_service(service, context.forms);
  const [result] = await Promise.all([
    fetch_latest_service_result(service, context.entity_profile_id),
  ]);
  const status_record = status_for_service(context.statuses, runnable_form, service);
  const log = latest_log_for_service(context.logs, service);
  const raw_status = result?.status || status_record?.status || log?.status || 'Not Run';
  const has_run = Boolean(result || status_record || log);

  return {
    id: `verification-${service.id}`,
    service,
    service_id: service.id,
    name: service.name,
    description: service.description || 'Checks submitted case facts against the configured verification provider.',
    application_form_id: application_form_id(runnable_form),
    runnable: Boolean(runnable_form && service_run_endpoint(service)),
    has_run,
    status: has_run ? title_case(raw_status) : 'Not Run',
    tone: has_run ? status_tone(raw_status) : 'neutral',
    last_run: format_timestamp(status_record?.timestamp || log?.created_at),
    rows: result_rows(result),
    message: result?.message || log?.message || '',
  };
}

export async function fetch_case_verification_checks({
  entity_type_id,
  entity_profile_id,
  project_id = null,
  forms = [],
} = {}) {
  if (!entity_type_id || !entity_profile_id || !forms.length) return [];
  const [services, statuses, logs] = await Promise.all([
    fetch_bound_services(entity_type_id, project_id),
    fetch_form_statuses(forms),
    fetch_verification_logs(entity_profile_id),
  ]);
  const context = { entity_profile_id, forms, statuses, logs };
  return Promise.all(services.map((service) => verification_check(service, context)));
}

export async function run_case_verification_check(check = {}) {
  const endpoint = service_run_endpoint(check?.service);
  if (!endpoint) throw new Error(`No execution endpoint is configured for ${check?.name || 'this verification service'}.`);
  if (!check?.application_form_id) throw new Error(`${check?.name || 'This verification service'} is not mapped to a submitted case form.`);

  const response = await axios.post(endpoint, {
    service_id: Number(check.service_id),
    application_form_id: Number(check.application_form_id),
  }, get_request_config());
  return payload_data(response);
}
